<template>
  <div>
    <Nav v-bind:organization="organization" />
    <div class="app">
      <Sidebar />
      <div id="organization_main_view">
          <div class="floating-container">
            <h1>Site Settings</h1>
            <p>To view and edit site settings, click <router-link v-bind:to="`/organizations/me/sites/${site.public_key}/settings/`">here</router-link> to go to the settings dashboard.
            </p>
          </div>
          <div class="floating-container">
            <h1>Site Testing</h1>
            <p>To view and edit site testing strategies, click <router-link v-bind:to="`/organizations/me/strategies/`">here</router-link> to go to the testing dashboard.
            </p>
          </div>
          <div class="floating-container">
            <div class="floating-header">
              <h1>Articles</h1>
              <button class="small" @click="addArticle">+ Add Article</button>
              <router-link to="/organizations/me/"><button class="small">← Go Back</button></router-link>
              <div style="clear: both"></div>
            </div>
            <Table v-bind:head="articles.head" v-bind:body="articles.body" name="articles" />
          </div>
          <div class="floating-container">
            <div class="floating-header">
              <h1>Subscriptions</h1>
              <button class="small" @click="addSubscription">+ Add Subscription</button>
              <div style="clear: both"></div>
            </div>
            <Table v-bind:head="subscriptions.head" v-bind:body="subscriptions.body" name="subscriptions" />
          </div>
      </div>
    </div>
  </div>
</template>

<script>
import "../css/main.css";
import "../css/components.css";

import request from "../js/request.js";
import utils from "../js/utils.js";

import Table from "./generics/Table";
import Sidebar from "./generics/Sidebar";
import Nav from "./generics/Nav";
import OrganizationSettingsForm from "./special/OrganizationSettingsForm";

import store from '@/js/store';

const { api_path } = utils;

const methods = {
  process: {
    articles: (site, articles) => {
      return articles[site.public_key].map(o => {
        return {
          key: o.public_key,
          data: [
            { html: o.article_name },
            { html: site.site_name },
            { html: `<code>${o.public_key}</code>` },
            { html: utils.formatTime(new Date(o.createdAt)) },
            { html: o.price.toFixed(2) },
            { html: "$" + (o.price * o.num_purchases).toFixed(2) },
          ],
        };
      });
    },
    subscriptions: (site, subscriptions) => {
      return subscriptions[site.public_key].map(o => {
        return {
          key: o.public_key,
          data: [
            { html: o.subscription_name },
            { html: site.site_name },
            { html: `<code>${o.public_key}</code>` },
            { html: o.price.toFixed(2) },
            { html: o.duration.value + " " + o.duration.unit },
            { html: `<button>Delete</button>` },
          ],
        };
      });
    },
  }
}

export default {
  name: 'OrganizationMainView',
  data () {
    const data = {
      organization: {},
      articles: {
        head: [
          { name: "article_name", placeholder: "Article Title", html: "Article", width: 350 },
          { html: "Site", width: 200 },
          { html: "Public Key", width: 350 },
          { html: "Created On", width: 100 },
          { name: "price", class: "money", placeholder: "0.49", html: "Price", width: 100, align: "right" },
          { html: "Revenue", width: 98, align: "right" },
        ],
        body: [],
      },
      subscriptions: {
        head: [
          { name: "subscription_name", placeholder: "Subscription Name", html: "Subscription", width: 350 },
          { html: "Site", width: 200 },
          { html: "Public Key", width: 350 },
          { name: "price", class: "money", align: "right", placeholder: "9.99", html: "Price", width: 100 },
          { name: "duration", placeholder: "10 Days", html: "Duration", width: 100, align: "right" },
          { html: "Actions", width: 98, align: "right" },
        ],
        body: [],
      },
      site: {},
    };

    (async () => {
      const { public_key } = this.$route.params;
      const { organization, sites, articles, subscriptions } = await utils.generalInformation({ site_public_key: public_key });

      data.organization = organization;
      data.site = sites.find(o => o.public_key === public_key);

      data.articles.body = methods.process.articles(data.site, articles);
      data.subscriptions.body = methods.process.subscriptions(data.site, subscriptions);
    })();

    return data;
  },
  methods: {
    addArticle: (() => {
      let editing = false;
      return async function (e) {
        if (editing === false) {
          e.target.innerText = "Save Article";
          editing = true;
          this.$data.articles.body.unshift({ empty: true });
        } else {
          let table = e.target.parentNode.nextElementSibling;
          let data = utils.getFormInput(table);
          data.price = +data.price;

          if (data.article_name.length > 0 && !isNaN(data.price)) {
            let response = await request(`${api_path.pay}/organizations/me/sites/${this.$data.site.public_key}/articles/`, {
              headers: { Authorization: `Basic ${store.organization.auth_token}` }
            }).post(data);

            const { articles } = await utils.generalInformation({ site_public_key: this.$data.site.public_key, update: ["articles"] });

            this.$data.articles.body.pop();

            editing = false;
            e.target.innerText = "+ Add Article";

            this.$data.articles.body = methods.process.articles(this.$data.site, articles);
          }
        }
      }
    })(),

    addSubscription: (() => {
      let editing = false;
      return async function (e) {
        if (editing === false) {
          e.target.innerText = "Save Subscription";
          editing = true;
          this.$data.subscriptions.body.unshift({ empty: true });
        } else {
          let table = e.target.parentNode.nextElementSibling;
          let data = utils.getFormInput(table);

          data.price = +data.price;
          data.duration = utils.subscription.labelToDuration(data.duration);

          if (data.subscription_name.length > 0 && !isNaN(data.price)) {
            let response = await request(`${api_path.pay}/organizations/me/sites/${this.$data.site.public_key}/subscriptions/`, {
              headers: { Authorization: `Basic ${store.organization.auth_token}` }
            }).post(data);

            const { subscriptions } = await utils.generalInformation({ site_public_key: this.$data.site.public_key, update: ["subscriptions"] });

            this.$data.subscriptions.body.pop();

            editing = false;
            e.target.innerText = "+ Add Subscription";

            this.$data.subscriptions.body = methods.process.subscriptions(this.$data.site, subscriptions);
          }
        }
      }
    })(),
  },

  mounted () {
    this.$on("edit", payload => {
      let { idx, name } = payload;
      let { body, head } = this.$data[name];

      body[idx].data = body[idx].data.map((o, col_idx) => {
        return Object.assign(o, { edit: !!head[col_idx].name });
      });
      this.$data[name].body = body.map(o => o);
    });

    this.$on("save", async payload => {
      let { data, idx, $row, key, name } = payload;

      let record = this.$data[name][idx];

      if (name === "articles") {
        let response = await request(`${api_path.pay}/organizations/me/sites/${this.$data.site.public_key}/articles/${key}`, {
          headers: { Authorization: `Basic ${store.organization.auth_token}` }
        }).put(data);

        const { articles } = await utils.generalInformation({ site_public_key: this.$data.site.public_key, update: ["articles"] });
        this.$data.articles.body = methods.process.articles(this.$data.site, articles);
        console.log(this.$data.articles.body, articles);
      } else if (name === "subscriptions") {
        data.price = +data.price;
        data.duration = utils.subscription.labelToDuration(data.duration);

        let response = await request(`${api_path.pay}/organizations/me/sites/${this.$data.site.public_key}/subscriptions/${key}/`, {
          headers: { Authorization: `Basic ${store.organization.auth_token}` }
        }).put(data);

        const { subscriptions } = await utils.generalInformation({ site_public_key: this.$data.site.public_key, update: ["subscriptions"] });
        this.$data.subscriptions.body = methods.process.subscriptions(this.$data.site, subscriptions);
      }
    });
  },

  components: {
    Table,
    OrganizationSettingsForm,
    Sidebar,
    Nav,
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.app {
  text-align: center;
  margin-top: 20px;
}

p {
  color: #2c3e50b0;
}

#organization_main_view {
  display: inline-block;
  text-align: left;
}
</style>



// WEBPACK FOOTER //
// src/components/OrganizationSite.vue